import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { z } from 'zod';

import { apiClient } from '@/lib/api-client';
import { useAuthStore } from '@/stores/authStore';

import { canViewCopingTypes } from '../constants/copingTypes';

import { clientsKeys } from './useClients';

const copingTypesSchema = z.object({
  copingTypes: z.array(z.string()),
  changes: z.array(
    z.object({
      id: z.string(),
      source: z.enum(['session', 'chat_session', 'profile']),
      sessionId: z.string().nullable(),
      previousCopingTypes: z.array(z.string()),
      copingTypes: z.array(z.string()),
      changedByName: z.string(),
      createdAt: z.string(),
    }),
  ),
});
export type CopingTypeChange = z.infer<typeof copingTypesSchema>['changes'][number];

const copingTypesKey = (clientId: string) => clientsKeys.operation('coping-types', clientId);

/** The client's current coping types and change history. */
export function useCopingTypes(clientId: string | null | undefined) {
  const role = useAuthStore((s) => s.user?.role);
  return useQuery({
    queryKey: copingTypesKey(clientId ?? ''),
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: unknown }>(
        `/clients/${clientId ?? ''}/coping-types`,
      );
      return copingTypesSchema.parse(data.data);
    },
    enabled: Boolean(clientId) && canViewCopingTypes(role),
  });
}

export function useUpdateCopingTypes(clientId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { copingTypes: string[]; sessionId?: string; chatSessionId?: string }) =>
      apiClient.put(`/clients/${clientId}/coping-types`, input),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: copingTypesKey(clientId) }),
        qc.invalidateQueries({ queryKey: clientsKeys.lists() }),
      ]);
      toast.success('Coping types updated.');
    },
  });
}
